import type { CreateGenerationRequest } from '@/api/generation';

export type LocalPendingGeneration = {
  user_id: string;
  gen_id?: string;
  poll_timeout_seconds?: number;
  idempotency_key: string;
  payload: CreateGenerationRequest;
  source?: string;
  fingerprint?: string;
  submission_state?: 'sending' | 'ack_unknown' | 'reconciling' | 'accepted';
  reconcile_attempts?: number;
  next_reconcile_at?: number;
  created_at: number;
  updated_at: number;
};

type PendingOutbox = Record<string, LocalPendingGeneration>;

const LEGACY_PENDING_KEY = 'glowpic:generate:pending';
const LOCAL_OUTBOX_KEY = 'glowpic:generate:outbox:v2';
const LOCAL_PENDING_TTL_MS = 24 * 60 * 60 * 1000;

function entryKey(pending: Pick<LocalPendingGeneration, 'user_id' | 'idempotency_key'>): string {
  return `${pending.user_id}:${pending.idempotency_key}`;
}

export function generationPayloadFingerprint(payload: CreateGenerationRequest): string {
  return JSON.stringify({
    prompt: payload.prompt.trim(),
    style_id: payload.style_id || null,
    ratio: payload.ratio,
    reference_image_url: payload.reference_image_url || null,
    secondary_image_url: payload.secondary_image_url || null,
    additional_image_urls: payload.additional_image_urls ?? [],
    model_id: payload.model_id || null,
    quality: payload.quality || null,
    template_id: payload.template_id || null,
    parent_gen_id: payload.parent_gen_id || null,
    tool_id: payload.tool_id || null,
    mode: payload.mode || null,
    entry_id: payload.entry_id || null,
    generation_operation: payload.generation_operation || null,
    pose: payload.pose || null,
  });
}

function readOutbox(): PendingOutbox {
  try {
    const raw = localStorage.getItem(LOCAL_OUTBOX_KEY);
    const parsed = raw ? JSON.parse(raw) as PendingOutbox : {};
    const now = Date.now();
    const valid = Object.fromEntries(Object.entries(parsed).filter(([, item]) => (
      item?.user_id
      && item?.idempotency_key
      && item?.payload
      && now - item.created_at <= LOCAL_PENDING_TTL_MS
    )));
    const legacyRaw = localStorage.getItem(LEGACY_PENDING_KEY);
    if (legacyRaw) {
      const legacy = JSON.parse(legacyRaw) as LocalPendingGeneration;
      if (
        legacy?.user_id
        && legacy?.idempotency_key
        && legacy?.payload
        && now - legacy.created_at <= LOCAL_PENDING_TTL_MS
      ) {
        valid[entryKey(legacy)] = legacy;
      }
      localStorage.removeItem(LEGACY_PENDING_KEY);
    }
    writeOutbox(valid);
    return valid;
  } catch {
    localStorage.removeItem(LOCAL_OUTBOX_KEY);
    localStorage.removeItem(LEGACY_PENDING_KEY);
    return {};
  }
}

function writeOutbox(outbox: PendingOutbox): void {
  if (Object.keys(outbox).length === 0) {
    localStorage.removeItem(LOCAL_OUTBOX_KEY);
    return;
  }
  localStorage.setItem(LOCAL_OUTBOX_KEY, JSON.stringify(outbox));
}

export function readLocalPendings(userId?: string): LocalPendingGeneration[] {
  return Object.values(readOutbox())
    .filter((item) => !userId || item.user_id === userId)
    .sort((a, b) => a.created_at - b.created_at);
}

export function readLocalPending(): LocalPendingGeneration | null {
  return readLocalPendings()[0] ?? null;
}

export function writeLocalPending(pending: LocalPendingGeneration): void {
  const outbox = readOutbox();
  const updated = {
    ...pending,
    fingerprint: pending.fingerprint ?? generationPayloadFingerprint(pending.payload),
    submission_state: pending.gen_id
      ? 'accepted' as const
      : pending.submission_state ?? 'sending' as const,
    updated_at: Date.now(),
  };
  outbox[entryKey(updated)] = updated;
  writeOutbox(outbox);
}

export function clearLocalPending(genId?: string): void {
  // An unknown Create ACK has no gen_id yet. Never let an unrelated error erase
  // the whole outbox: recovery must retain the original idempotency key.
  if (!genId) return;
  const outbox = readOutbox();
  for (const [key, item] of Object.entries(outbox)) {
    if (item.gen_id === genId) delete outbox[key];
  }
  writeOutbox(outbox);
}

export function clearLocalPendingByIdempotencyKey(userId: string, idempotencyKey: string): void {
  const outbox = readOutbox();
  delete outbox[entryKey({ user_id: userId, idempotency_key: idempotencyKey })];
  writeOutbox(outbox);
}

export function clearUnresolvedLocalPendingsForDurableTasks(
  userId: string,
  tasks: Array<{ client_request_id?: string }>,
): void {
  const outbox = readOutbox();
  let changed = false;
  for (const task of tasks) {
    if (!task.client_request_id) continue;
    const key = entryKey({ user_id: userId, idempotency_key: task.client_request_id });
    const pending = outbox[key];
    if (pending && !pending.gen_id) {
      delete outbox[key];
      changed = true;
    }
  }
  if (changed) writeOutbox(outbox);
}

export function findUnresolvedLocalPending(
  userId: string,
  payload: CreateGenerationRequest,
): LocalPendingGeneration | null {
  const fingerprint = generationPayloadFingerprint(payload);
  return readLocalPendings(userId).find((item) => (
    !item.gen_id && (item.fingerprint ?? generationPayloadFingerprint(item.payload)) === fingerprint
  )) ?? null;
}

export function markLocalPendingForReconcile(
  userId: string,
  idempotencyKey: string,
  submissionState: 'ack_unknown' | 'reconciling',
  delayMs = 0,
): LocalPendingGeneration | null {
  const outbox = readOutbox();
  const key = entryKey({ user_id: userId, idempotency_key: idempotencyKey });
  const current = outbox[key];
  if (!current) return null;
  const updated: LocalPendingGeneration = {
    ...current,
    submission_state: submissionState,
    reconcile_attempts: submissionState === 'reconciling'
      ? (current.reconcile_attempts ?? 0) + 1
      : current.reconcile_attempts ?? 0,
    next_reconcile_at: Date.now() + delayMs,
    updated_at: Date.now(),
  };
  outbox[key] = updated;
  writeOutbox(outbox);
  return updated;
}

export function restartLocalPendingReconcile(
  userId: string,
  idempotencyKey: string,
): LocalPendingGeneration | null {
  const outbox = readOutbox();
  const key = entryKey({ user_id: userId, idempotency_key: idempotencyKey });
  const current = outbox[key];
  if (!current || current.gen_id) return null;
  const updated: LocalPendingGeneration = {
    ...current,
    submission_state: 'ack_unknown',
    reconcile_attempts: 0,
    next_reconcile_at: Date.now(),
    updated_at: Date.now(),
  };
  outbox[key] = updated;
  writeOutbox(outbox);
  return updated;
}

export function isLocalPendingReadyForReconcile(
  pending: LocalPendingGeneration,
  now: number,
  maxAttempts: number,
): boolean {
  return !pending.gen_id
    && pending.submission_state !== 'sending'
    && (pending.reconcile_attempts ?? 0) < maxAttempts
    && (pending.next_reconcile_at ?? 0) <= now;
}

export function isLocalPendingCompatible(
  pending: LocalPendingGeneration | null,
  payload: CreateGenerationRequest,
): boolean {
  if (!pending) return false;
  return generationPayloadFingerprint(pending.payload) === generationPayloadFingerprint(payload);
}
